import 'leaflet/dist/leaflet.css';
import 'leaflet-defaulticon-compatibility/dist/leaflet-defaulticon-compatibility.webpack.css'; // Re-uses images from ~leaflet package
import { useCallback, useEffect, useMemo, useRef } from 'react';
import L from 'leaflet';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import 'leaflet-defaulticon-compatibility';
import { hasValidCoords, LocationFormValue } from './location-form-value';

const DEFAULT_CENTER: [number, number] = [-37.8136, 144.9631]; // Melbourne
const DEFAULT_ZOOM = 13;

function MapViewUpdater({ latitude, longitude }: LocationFormValue) {
  const map = useMap();
  const prevCoordsRef = useRef<string>('');

  useEffect(() => {
    if (hasValidCoords(latitude, longitude)) {
      const coordsKey = `${latitude},${longitude}`;
      if (coordsKey !== prevCoordsRef.current) {
        const isInitialLoad = prevCoordsRef.current === '';
        prevCoordsRef.current = coordsKey;
        if (isInitialLoad) {
          map.setView([latitude!, longitude!], map.getZoom());
        } else {
          map.flyTo([latitude!, longitude!], map.getZoom());
        }
      }
    }
  }, [latitude, longitude, map]);

  return null;
}

function MapClickHandler({ onMapClick, active }: { onMapClick: (e: L.LeafletMouseEvent) => void; active: boolean }) {
  const map = useMap();

  useEffect(() => {
    if (active) {
      map.on('click', onMapClick);
    } else {
      map.off('click', onMapClick);
    }
    return () => {
      map.off('click', onMapClick);
    };
  }, [map, onMapClick, active]);

  return null;
}

export type LeafletMapProps = LocationFormValue & {
  onCoordinatesChange: (latitude: number, longitude: number) => void;
  readOnly?: boolean;
};

export function DynamicFormLeafletMap({ latitude, longitude, onCoordinatesChange, readOnly = false }: LeafletMapProps) {
  const isCoordsValid = hasValidCoords(latitude, longitude);

  const center: [number, number] = isCoordsValid ? [Number(latitude), Number(longitude)] : DEFAULT_CENTER;

  const markerRef = useRef<L.Marker>(null);

  const eventHandlers = useMemo(
    () => ({
      dragend() {
        const marker = markerRef.current;
        if (marker && !readOnly) {
          const position = marker.getLatLng();
          onCoordinatesChange(position.lat, position.lng);
        }
      }
    }),
    [onCoordinatesChange, readOnly]
  );

  const onMapClick = useCallback(
    (e: L.LeafletMouseEvent) => {
      onCoordinatesChange(e.latlng.lat, e.latlng.lng);
    },
    [onCoordinatesChange]
  );

  return (
    <MapContainer center={center} zoom={DEFAULT_ZOOM} style={{ height: '300px', width: '100%', borderRadius: '4px' }}>
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      <Marker
        draggable={!readOnly}
        eventHandlers={eventHandlers}
        position={isCoordsValid ? center : DEFAULT_CENTER}
        ref={markerRef}
      />
      <MapViewUpdater
        latitude={isCoordsValid ? Number(latitude) : undefined}
        longitude={isCoordsValid ? Number(longitude) : undefined}
      />
      <MapClickHandler onMapClick={onMapClick} active={!readOnly} />
    </MapContainer>
  );
}
